/* dusa: the PDP gallery ("Showroom") over Dawn's <media-gallery>; markup in snippets/dusa-gallery.liquid.
   - The photos sit in one horizontal scroll-snap track; arrows, thumbnails, keys and swipes all move it.
   - A size or colour change re-renders the slide list (product-info.js updateMedia), so thumbnails and chips
     rebuild from the live list and the variant's photo (Dawn marks it .is-active) is shown.
   - Desktop: hovering the photo magnifies it 2.2x under the pointer; the larger file loads on first hover.
   - A click or tap opens the full-screen viewer, built on first open: swipe or arrows between photos, tap to
     zoom 2.5x where tapped and drag to look around, Escape or Back to close. */
(function () {
  const desktop = window.matchMedia('(min-width: 750px)');
  const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const HOVER_ZOOM = 2.2;
  const VIEWER_ZOOM = 2.5;
  const FILTERS = [
    ['door', 'At the door'],
    ['scale', 'To scale'],
    ['close', 'Close-up'],
    ['size', 'Sizes'],
  ];

  const behavior = () => (reducedMotion.matches ? 'auto' : 'smooth');
  const sized = (url, width) => url.replace(/([?&]width=)\d+/, `$1${width}`);
  const escapeHtml = (text) =>
    String(text).replace(/[&<>"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[char]);

  function enhance(gallery) {
    if (gallery.dataset.dusaReady) return;
    const track = gallery.querySelector('[data-dusa-track]');
    if (!track) return;
    gallery.dataset.dusaReady = 'true';

    const stage = gallery.querySelector('[data-dusa-stage]');
    const thumbs = gallery.querySelector('[data-dusa-thumbs]');
    const chips = gallery.querySelector('[data-dusa-chips]');
    const count = gallery.querySelector('[data-dusa-count]');
    const caption = gallery.querySelector('[data-dusa-caption-text]');
    const progress = gallery.querySelector('[data-dusa-progress]');
    const thumbLabel = (gallery.querySelector('.dusa-gallery__thumb') || {}).ariaLabel || '';

    let index = 0;
    let filter = 'all';
    let activeId = '';

    const slides = () => Array.from(track.querySelectorAll(':scope > li[data-media-id]'));
    const visible = () => slides().filter((slide) => !slide.hidden);

    // Photos after the first wait in data-src/data-srcset (see dusa-gallery.liquid) until shown or next to the one shown.
    function load(slide) {
      const image = slide && slide.querySelector('img[data-src]');
      if (!image) return;
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      image.src = image.dataset.src;
      image.removeAttribute('data-src');
      image.removeAttribute('data-srcset');
    }

    function show(next, how) {
      const list = visible();
      if (!list.length) return;
      index = (next + list.length) % list.length;
      const slide = list[index];
      [index, index + 1, index - 1].forEach((n) => load(list[(n + list.length) % list.length]));
      if (how) track.scrollTo({ left: slide.offsetLeft, behavior: how });
      resetHoverZoom();

      activeId = slide.dataset.mediaId;
      slides().forEach((item) => item.classList.toggle('is-active', item === slide));

      if (count) count.textContent = `${index + 1} / ${list.length}`;
      if (caption) caption.textContent = slide.dataset.dusaCaption || '';
      if (progress) progress.style.width = `${((index + 1) / list.length) * 100}%`;

      if (thumbs) {
        Array.from(thumbs.querySelectorAll('.dusa-gallery__thumb')).forEach((button, position) => {
          if (position === index) button.setAttribute('aria-current', 'true');
          else button.removeAttribute('aria-current');
        });
        const current = thumbs.children[index];
        if (current) {
          const vertical = thumbs.scrollHeight > thumbs.clientHeight + 1 && thumbs.scrollWidth <= thumbs.clientWidth + 1;
          if (vertical) {
            const top = current.offsetTop - (thumbs.clientHeight - current.offsetHeight) / 2;
            thumbs.scrollTo({ top, behavior: how ? behavior() : 'auto' });
          } else {
            const left = current.offsetLeft - (thumbs.clientWidth - current.offsetWidth) / 2;
            thumbs.scrollTo({ left, behavior: how ? behavior() : 'auto' });
          }
        }
      }
    }

    function buildThumbs() {
      if (!thumbs) return;
      thumbs.innerHTML = visible()
        .map((slide, position) => {
          const src = slide.dataset.dusaThumb || '';
          const label = thumbLabel.replace(/\d+/, String(position + 1));
          return `<li><button type="button" class="dusa-gallery__thumb" aria-label="${escapeHtml(label)}"><img src="${escapeHtml(
            sized(src, 160)
          )}" srcset="${escapeHtml(sized(src, 80))} 80w, ${escapeHtml(
            sized(src, 160)
          )} 160w" sizes="76px" width="160" height="160" loading="lazy" alt=""></button></li>`;
        })
        .join('');
    }

    function buildChips() {
      if (!chips) return;
      const all = slides();
      const groups = FILTERS.map(([key, label]) => [key, label, all.filter((slide) => slide.dataset.dusaTag === key).length]).filter(
        ([, , n]) => n > 0
      );
      const useful = all.length >= 4 && groups.length >= 2;
      chips.hidden = !useful;
      if (!useful) {
        chips.innerHTML = '';
        return;
      }
      chips.innerHTML = [['all', 'All', all.length], ...groups]
        .map(
          ([key, label, n]) =>
            `<button type="button" class="dusa-gallery__chip" data-dusa-filter="${key}" aria-pressed="${key === filter}">${label} <span>${n}</span></button>`
        )
        .join('');
    }

    function applyFilter(next) {
      filter = next;
      slides().forEach((slide) => {
        slide.hidden = filter !== 'all' && slide.dataset.dusaTag !== filter;
      });
      if (chips) {
        chips.querySelectorAll('[data-dusa-filter]').forEach((chip) => {
          chip.setAttribute('aria-pressed', String(chip.dataset.dusaFilter === filter));
        });
      }
      buildThumbs();
      show(0, 'auto');
    }

    // The slide list changed (a size or colour pick): start again from the variant's photo.
    function rebuild() {
      filter = 'all';
      slides().forEach((slide) => {
        slide.hidden = false;
      });
      gallery.classList.toggle('dusa-gallery--single', slides().length < 2);
      buildChips();
      buildThumbs();
      const list = visible();
      const active = list.findIndex((slide) => slide.classList.contains('is-active'));
      show(Math.max(0, active), 'auto');
    }

    gallery.querySelectorAll('[data-dusa-step]').forEach((button) => {
      button.addEventListener('click', () => show(index + Number(button.dataset.dusaStep), behavior()));
    });

    if (thumbs) {
      thumbs.addEventListener('click', (event) => {
        const button = event.target.closest('.dusa-gallery__thumb');
        if (!button) return;
        show(Array.from(thumbs.children).indexOf(button.parentElement), behavior());
      });
    }

    if (chips) {
      chips.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-dusa-filter]');
        if (chip && chip.dataset.dusaFilter !== filter) applyFilter(chip.dataset.dusaFilter);
      });
    }

    gallery.addEventListener('keydown', (event) => {
      if (event.target.closest('input, select, textarea')) return;
      if (event.key === 'ArrowRight') show(index + 1, behavior());
      else if (event.key === 'ArrowLeft') show(index - 1, behavior());
      else return;
      event.preventDefault();
    });

    // Follow swipes and trackpad scrolls.
    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const width = track.clientWidth || 1;
          const next = Math.round(track.scrollLeft / width);
          if (next !== index && next < visible().length) show(next);
        });
      },
      { passive: true }
    );

    // Hover magnifier (desktop, mouse): the photo scales up under the pointer.
    let zoomed = null;
    function resetHoverZoom() {
      if (!zoomed) return;
      zoomed.style.transform = '';
      zoomed.style.transformOrigin = '';
      zoomed = null;
      stage.classList.remove('is-zooming');
    }
    stage.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse' || !fineHover.matches || !desktop.matches) return;
      if (event.target.closest('.dusa-gallery__nav')) {
        resetHoverZoom();
        return;
      }
      const slide = visible()[index];
      const image = slide && !slide.hasAttribute('data-dusa-rich') && slide.querySelector('.dusa-gallery__open img');
      if (!image) return;
      if (zoomed !== image) {
        resetHoverZoom();
        load(slide);
        zoomed = image;
        const wanted = `${Math.round(stage.clientWidth * HOVER_ZOOM)}px`;
        if (image.getAttribute('sizes') !== wanted) image.setAttribute('sizes', wanted);
      }
      const box = slide.getBoundingClientRect();
      image.style.transformOrigin = `${((event.clientX - box.left) / box.width) * 100}% ${((event.clientY - box.top) / box.height) * 100}%`;
      image.style.transform = `scale(${HOVER_ZOOM})`;
      stage.classList.add('is-zooming');
    });
    stage.addEventListener('pointerleave', resetHoverZoom);

    track.addEventListener('click', (event) => {
      const opener = event.target.closest('.dusa-gallery__open');
      if (!opener) return;
      const list = visible().filter((slide) => !slide.hasAttribute('data-dusa-rich'));
      const at = list.indexOf(opener.closest('li'));
      if (at < 0) return;
      resetHoverZoom();
      viewer().open(list, at, opener, (shown) => show(visible().indexOf(shown), 'auto'));
    });

    // product-info.js moves, adds and removes slides and marks the variant's photo .is-active.
    new MutationObserver(rebuild).observe(track, { childList: true });
    new MutationObserver(() => {
      const active = slides().find((slide) => slide.classList.contains('is-active'));
      if (!active || active.dataset.mediaId === activeId) return;
      if (active.hidden) applyFilter('all');
      show(visible().indexOf(active), 'auto');
    }).observe(track, { subtree: true, attributes: true, attributeFilter: ['class'] });

    rebuild();
  }

  /* ---------- full-screen viewer (one per page, built on first open) ---------- */
  let viewerInstance = null;
  function viewer() {
    if (viewerInstance) return viewerInstance;

    const root = document.createElement('div');
    root.className = 'dusa-viewer';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Photo viewer');
    root.innerHTML = `
      <div class="dusa-viewer__bar">
        <span class="dusa-viewer__count" data-count></span>
        <button type="button" class="dusa-viewer__close" data-close aria-label="Close">
          <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true" focusable="false"><path d="M3 3l12 12M15 3L3 15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
        </button>
      </div>
      <div class="dusa-viewer__body">
        <ul class="dusa-viewer__track list-unstyled" data-track></ul>
        <button type="button" class="dusa-viewer__nav dusa-viewer__nav--prev" data-step="-1" aria-label="Previous photo">
          <svg viewBox="0 0 10 16" width="10" height="16" aria-hidden="true" focusable="false"><path d="M8 2L2 8l6 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <button type="button" class="dusa-viewer__nav dusa-viewer__nav--next" data-step="1" aria-label="Next photo">
          <svg viewBox="0 0 10 16" width="10" height="16" aria-hidden="true" focusable="false"><path d="M2 2l6 6-6 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>
      <div class="dusa-viewer__foot">
        <p class="dusa-viewer__caption" data-caption></p>
        <ul class="dusa-viewer__thumbs list-unstyled" data-thumbs></ul>
      </div>`;
    document.body.appendChild(root);

    const track = root.querySelector('[data-track]');
    const thumbs = root.querySelector('[data-thumbs]');
    const countEl = root.querySelector('[data-count]');
    const captionEl = root.querySelector('[data-caption]');
    const closeButton = root.querySelector('[data-close]');

    let items = [];
    let index = 0;
    let opener = null;
    let onClose = null;
    let pushed = false;
    let zoom = null; // { image, x, y, width, height }

    function render(list) {
      items = list.map((slide) => {
        const image = slide.querySelector('img');
        return {
          slide,
          src: slide.dataset.dusaFull || (image && image.currentSrc) || '',
          thumb: slide.dataset.dusaThumb || '',
          alt: image ? image.alt : '',
          caption: slide.dataset.dusaCaption || '',
          width: image ? Number(image.getAttribute('width')) || 0 : 0,
          height: image ? Number(image.getAttribute('height')) || 0 : 0,
        };
      });
      track.innerHTML = items
        .map((item) => {
          const srcset = [800, 1200, 1600, 2048].map((w) => `${escapeHtml(sized(item.src, w))} ${w}w`).join(', ');
          const size = item.width && item.height ? ` width="${item.width}" height="${item.height}"` : '';
          return `<li class="dusa-viewer__slide"><img src="${escapeHtml(sized(item.src, 1600))}" srcset="${srcset}" sizes="100vw"${size} alt="${escapeHtml(
            item.alt
          )}" loading="lazy" decoding="async" draggable="false"></li>`;
        })
        .join('');
      thumbs.innerHTML = items
        .map(
          (item, position) =>
            `<li><button type="button" class="dusa-viewer__thumb" aria-label="Photo ${position + 1}"><img src="${escapeHtml(
              sized(item.thumb, 120)
            )}" width="120" height="120" loading="lazy" alt=""></button></li>`
        )
        .join('');
      root.classList.toggle('dusa-viewer--single', items.length < 2);
    }

    function go(next, how) {
      if (!items.length) return;
      unzoom();
      index = (next + items.length) % items.length;
      const slide = track.children[index];
      if (how && slide) track.scrollTo({ left: slide.offsetLeft, behavior: how });
      // Load the photo shown and its neighbours.
      [index - 1, index, index + 1].forEach((n) => {
        const image = track.children[(n + items.length) % items.length]?.querySelector('img');
        if (image) image.loading = 'eager';
      });
      countEl.textContent = items.length > 1 ? `${index + 1} / ${items.length}` : '';
      captionEl.textContent = items[index].caption;
      Array.from(thumbs.querySelectorAll('button')).forEach((button, position) => {
        if (position === index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      const thumb = thumbs.children[index];
      if (thumb) thumbs.scrollTo({ left: thumb.offsetLeft - (thumbs.clientWidth - thumb.offsetWidth) / 2, behavior: how ? behavior() : 'auto' });
    }

    function open(list, at, from, closeCallback) {
      opener = from;
      onClose = closeCallback;
      render(list);
      root.hidden = false;
      document.body.classList.add('overflow-hidden');
      document.documentElement.classList.add('dusa-viewer-open');
      go(at);
      requestAnimationFrame(() => {
        const slide = track.children[index];
        if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
      });
      closeButton.focus({ preventScroll: true });
      try {
        history.pushState({ dusaViewer: true }, '');
        pushed = true;
      } catch (error) {
        pushed = false;
      }
    }

    function close(fromHistory) {
      if (root.hidden) return;
      unzoom();
      root.hidden = true;
      document.body.classList.remove('overflow-hidden');
      document.documentElement.classList.remove('dusa-viewer-open');
      const shown = items[index] && items[index].slide;
      if (onClose && shown) onClose(shown);
      const focusTarget = (shown && shown.querySelector('.dusa-gallery__open')) || opener;
      if (focusTarget) focusTarget.focus({ preventScroll: true });
      if (pushed && !fromHistory && history.state && history.state.dusaViewer) history.back();
      pushed = false;
    }

    // Zoom: 2.5x with the tapped point staying under the finger; drag to pan; tap again to fit.
    function clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    }
    function place() {
      const { image, x, y, width, height } = zoom;
      zoom.x = clamp(x, width * (1 - VIEWER_ZOOM), 0);
      zoom.y = clamp(y, height * (1 - VIEWER_ZOOM), 0);
      image.style.transform = `translate(${zoom.x}px, ${zoom.y}px) scale(${VIEWER_ZOOM})`;
    }
    function zoomAt(image, clientX, clientY) {
      const box = image.getBoundingClientRect();
      const px = clientX - box.left;
      const py = clientY - box.top;
      zoom = { image, width: box.width, height: box.height, x: px * (1 - VIEWER_ZOOM), y: py * (1 - VIEWER_ZOOM) };
      const wanted = `${Math.round(window.innerWidth * VIEWER_ZOOM)}px`;
      if (image.getAttribute('sizes') !== wanted) image.setAttribute('sizes', wanted);
      image.closest('li').classList.add('is-zoomed');
      root.classList.add('is-zoomed');
      place();
    }
    function unzoom() {
      if (!zoom) return;
      zoom.image.style.transform = '';
      zoom.image.closest('li').classList.remove('is-zoomed');
      root.classList.remove('is-zoomed');
      zoom = null;
    }

    let drag = null;
    track.addEventListener('pointerdown', (event) => {
      const image = event.target.closest('.dusa-viewer__slide img');
      if (!image) return;
      drag = { image, startX: event.clientX, startY: event.clientY, lastX: event.clientX, lastY: event.clientY, moved: false };
      if (zoom) image.setPointerCapture(event.pointerId);
    });
    track.addEventListener('pointermove', (event) => {
      if (!drag) return;
      if (Math.abs(event.clientX - drag.startX) + Math.abs(event.clientY - drag.startY) > 8) drag.moved = true;
      if (zoom && zoom.image === drag.image) {
        zoom.x += event.clientX - drag.lastX;
        zoom.y += event.clientY - drag.lastY;
        place();
      }
      drag.lastX = event.clientX;
      drag.lastY = event.clientY;
    });
    track.addEventListener('pointerup', (event) => {
      if (!drag) return;
      const { image, moved } = drag;
      drag = null;
      if (moved) return;
      if (zoom) unzoom();
      else zoomAt(image, event.clientX, event.clientY);
    });
    track.addEventListener('pointercancel', () => {
      drag = null;
    });

    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const next = Math.round(track.scrollLeft / (track.clientWidth || 1));
          if (next !== index && next < items.length) go(next);
        });
      },
      { passive: true }
    );

    root.querySelectorAll('[data-step]').forEach((button) => {
      button.addEventListener('click', () => go(index + Number(button.dataset.step), behavior()));
    });
    thumbs.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (button) go(Array.from(thumbs.children).indexOf(button.parentElement), behavior());
    });
    closeButton.addEventListener('click', () => close(false));
    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (zoom) unzoom();
        else close(false);
      } else if (event.key === 'ArrowRight') go(index + 1, behavior());
      else if (event.key === 'ArrowLeft') go(index - 1, behavior());
      else if (event.key === 'Tab') {
        const focusable = Array.from(root.querySelectorAll('button')).filter((el) => el.offsetParent !== null);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          last.focus();
          event.preventDefault();
        } else if (!event.shiftKey && document.activeElement === last) {
          first.focus();
          event.preventDefault();
        }
      } else return;
      if (event.key !== 'Tab') event.preventDefault();
    });
    window.addEventListener('popstate', () => close(true));

    viewerInstance = { open, close };
    return viewerInstance;
  }

  function init() {
    document.querySelectorAll('media-gallery.dusa-gallery').forEach(enhance);
  }

  // Quick add re-injects this script with each product it opens, so run init every time.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  if (!window.dusaGalleryListening) {
    window.dusaGalleryListening = true;
    document.addEventListener('shopify:section:load', init);
  }
})();
